import { useAppDispatch, useAppSelector } from "app/hooks";
import { getDeviceActionState, sortDevices } from "app/reducers/devicesSlice";
import { fetchDevices } from "app/thunks/devicesAPI";
import classNames from "classnames";
import { Headset } from "interfaces/Device";
import { useEffect, useState } from "react";
import { Title } from "ui";
import { Table, TableBody, TableHead, TableRow } from "ui/Table";
import ColumnHeader, { SortKey, SortOrder } from "ui/Table/ColumnHeader";
import { DEVICES_COLUMNS } from "utils/table/devices-table";

import { DevicesTableBodyRow } from "./DevicesTableBodyRow";

export const DevicesScreen = () => {
  const { devices } = useAppSelector(getDeviceActionState);
  const dispatch = useAppDispatch();

  const [initialLoad, setInitialLoad] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<SortKey>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("1");

  useEffect(() => {
    dispatch(fetchDevices()).then(() => setInitialLoad(true));
  }, [dispatch]);

  useEffect(() => {
    if (!initialLoad) return;

    const intervalId = setInterval(() => {
      dispatch(fetchDevices());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [dispatch, initialLoad]);

  useEffect(() => {
    dispatch(sortDevices({ sortBy, sortOrder }));
  }, [sortBy, dispatch, sortOrder]);

  /**
   * Called when table heading is clicked.
   * @param {SortKey} sortKey - string for what column to sort by
   */
  const handleSort = (sortKey: SortKey) => () => {
    if (!sortKey) {
      return;
    }

    let newSortDirection;

    // Default sort to -1 if we are sorting by a different key than we previously were
    if (sortKey !== sortBy) {
      newSortDirection = "-1";
    } else {
      // If toggling the same sort, switch the order
      newSortDirection = sortOrder === "-1" ? "1" : "-1";
    }

    setSortBy(sortKey);
    setSortOrder(newSortDirection as SortOrder);
  };

  return (
    <div className="flex flex-col justify-between w-full min-h-screen px-8 pb-8 bg-faux-dark-gunmetal pt-19">
      <div>
        <Title text="Dispositivos" />
        <Table>
          <TableHead>
            <TableRow>
              {DEVICES_COLUMNS.map((column, index) =>
                column.sortKey ? (
                  <ColumnHeader
                    key={index}
                    sortKey={column.sortKey}
                    sortingBy={sortBy}
                    sortDirection={sortOrder}
                    sortColumn={handleSort}
                    className={classNames(
                      "cursor-pointer",
                      index === 0 && "pl-2",
                      index === DEVICES_COLUMNS.length - 1 && "pr-2",
                    )}
                  >
                    {column.title}
                  </ColumnHeader>
                ) : (
                  <ColumnHeader
                    key={index}
                    isLast={DEVICES_COLUMNS.length - 1 === index}
                    className="pr-2"
                  >
                    {column.title}
                  </ColumnHeader>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody
            isLoading={!initialLoad}
            isEmpty={!devices?.length}
            columnAmount={DEVICES_COLUMNS.length + 1}
          >
            {devices?.map((device: Headset) => (
              <DevicesTableBodyRow key={device._id} headset={device} />
            ))}
          </TableBody>
        </Table>
      </div>
      {/* <div className="flex flex-row-reverse">
        <div
          className="flex items-center justify-center w-10 h-10 rounded-full cursor-pointer bg-faux-mountain-meadow"
          onClick={() => handleActionType(MODAL_TYPES.ADD)}
        >
          <AddIcon className="text-white" fontSize="small" />
        </div>
      </div> */}
    </div>
  );
};
